// ─── API Client ─────────────────────────────────────────────
// Garde le jeton courant (`mh_token`) et le secret de session (`mh_session`)
// dans localStorage. Le jeton dure 12 h ; passé ce délai, `ensureFreshToken`
// demande le code 2FA (modale globale, UNE seule demande en vol) et renouvelle
// le jeton par `POST /auth/reverify` — sans mot de passe, depuis la même IP.
// Une session révoquée / une IP changée renvoie à la page de connexion.
import { useSyncExternalStore } from 'react';

export const API_BASE = window.location.origin + '/api';

const TOKEN_KEY = 'mh_token';
const SESSION_KEY = 'mh_session';
const NOTICE_KEY = 'mh_login_notice';

const safe = (fn, fallback) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export const getAuthToken = () => safe(() => localStorage.getItem(TOKEN_KEY) || '', '');
export const setAuthToken = (token) =>
  safe(() => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  });
export const getSessionSecret = () => safe(() => localStorage.getItem(SESSION_KEY) || '', '');
export const setSessionSecret = (secret) =>
  safe(() => {
    if (secret) localStorage.setItem(SESSION_KEY, secret);
    else localStorage.removeItem(SESSION_KEY);
  });

/** Message à afficher sur la page de connexion après une déconnexion forcée (lu une fois). */
export const takeLoginNotice = () =>
  safe(() => {
    const v = sessionStorage.getItem(NOTICE_KEY) || '';
    sessionStorage.removeItem(NOTICE_KEY);
    return v;
  }, '');

const NOTICES = {
  ip_changed: 'Votre adresse IP a changé : reconnectez-vous.',
  session_revoked: 'Votre session a été fermée : reconnectez-vous.',
  session_required: 'Votre session a pris fin : reconnectez-vous.',
  '2fa_enrollment_required': 'Activez la vérification en deux étapes pour continuer.',
};

/** Efface jeton et session, mémorise le message, recharge (App affichera la connexion). */
export function forceLogout(notice) {
  setAuthToken('');
  setSessionSecret('');
  safe(() => {
    if (notice) sessionStorage.setItem(NOTICE_KEY, notice);
  });
  window.location.reload();
}

/** Déconnexion volontaire : révoque la session côté serveur (au mieux) puis nettoie. */
export async function logout() {
  const token = getAuthToken();
  if (token) {
    try {
      await fetch(API_BASE + '/auth/logout', {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: '{}',
      });
    } catch {
      /* hors ligne : la session expirera d'elle-même */
    }
  }
  forceLogout('');
}

// ─── Expiration du jeton ─────────────────────────────────────
function tokenExpiresAt(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(payload + '='.repeat((4 - (payload.length % 4)) % 4)));
    return typeof json.exp === 'number' ? json.exp * 1000 : 0;
  } catch {
    return 0;
  }
}
export const tokenExpired = (token, marginSeconds = 60) =>
  !token || tokenExpiresAt(token) - marginSeconds * 1000 <= Date.now();

// ─── Demande de code (état partagé avec <ReverifyModal/>) ─────
let reverifyState = { open: false, error: '', busy: false };
let inflight = null; // { promise, resolve }
const listeners = new Set();
const setReverify = (patch) => {
  reverifyState = { ...reverifyState, ...patch };
  listeners.forEach((l) => l());
};
const subscribeReverify = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export const useReverifyState = () => useSyncExternalStore(subscribeReverify, () => reverifyState);

function requestReverify() {
  if (inflight) return inflight.promise;
  let resolve;
  const promise = new Promise((r) => {
    resolve = r;
  });
  inflight = { promise, resolve };
  setReverify({ open: true, error: '', busy: false });
  return promise;
}

function finishReverify(token) {
  const cur = inflight;
  inflight = null;
  setReverify({ open: false, error: '', busy: false });
  if (cur) cur.resolve(token);
}

/** Appelée par la modale : envoie le code, renouvelle le jeton ou renvoie à la connexion. */
export async function submitReverifyCode(code) {
  if (reverifyState.busy) return;
  setReverify({ busy: true, error: '' });
  try {
    const res = await fetch(API_BASE + '/auth/reverify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: getSessionSecret(), code }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.token) {
      setAuthToken(data.token);
      finishReverify(data.token);
      return;
    }
    if (res.status === 400) {
      setReverify({ busy: false, error: 'Code invalide, réessayez.' });
      return;
    }
    if (res.status === 429) {
      setReverify({ busy: false, error: 'Trop de tentatives. Patientez avant de réessayer.' });
      return;
    }
    forceLogout(NOTICES[data.error] || 'Votre session a expiré : reconnectez-vous.');
  } catch {
    setReverify({ busy: false, error: 'Erreur de connexion au serveur.' });
  }
}

/**
 * Jeton utilisable : si le jeton est expiré (marge 60 s) et qu'une session existe,
 * demande le code 2FA. Sans session (ancienne connexion), rend le jeton tel quel :
 * le serveur répondra 401 et on retombera sur la connexion.
 */
export async function ensureFreshToken() {
  const token = getAuthToken();
  if (!token) return token;
  if (!tokenExpired(token) || !getSessionSecret()) return token;
  return requestReverify();
}

async function errorCode(res) {
  try {
    const j = await res.clone().json();
    return (j && j.error) || '';
  } catch {
    return '';
  }
}

export const api = {
  async fetch(path, options = {}, retried = false) {
    const token = await ensureFreshToken();
    const res = await fetch(API_BASE + path, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token
          ? {
              Authorization: 'Bearer ' + token,
            }
          : {}),
        ...options.headers,
      },
    });
    if (res.status === 401 || res.status === 403) {
      const code = await errorCode(res);
      if (NOTICES[code]) {
        forceLogout(NOTICES[code]);
        return res;
      }
      if (res.status === 401) {
        // Jeton expiré malgré la marge (horloge décalée) : un code, puis UNE nouvelle tentative.
        if (!retried && getSessionSecret() && tokenExpired(token, 0)) {
          await requestReverify();
          return this.fetch(path, options, true);
        }
        forceLogout('');
      }
    }
    return res;
  },
  async get(path) {
    const r = await this.fetch(path);
    if (!r.ok) return null;
    const t = await r.text();
    return t ? JSON.parse(t) : null;
  },
  async post(path, body) {
    const r = await this.fetch(path, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!r.ok) return null;
    const t = await r.text();
    return t ? JSON.parse(t) : null;
  },
  async put(path, body) {
    const r = await this.fetch(path, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    if (!r.ok) return null;
    const t = await r.text();
    return t ? JSON.parse(t) : null;
  },
  async patch(path, body) {
    const r = await this.fetch(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
    if (!r.ok) return null;
    const t = await r.text();
    return t ? JSON.parse(t) : null;
  },
  async del(path) {
    const r = await this.fetch(path, {
      method: 'DELETE',
    });
    return r.ok;
  },
};
